import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  TrendingUp, Eye, ShoppingCart, DollarSign, 
  MousePointer, ArrowUpRight, ArrowDownRight, 
  Calendar, RefreshCw, BarChart3, Target,
  Sparkles, ExternalLink
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConversionsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [landingPages, setLandingPages] = useState([]);

  useEffect(() => {
    fetchData();
    fetchLandingPages();
  }, [days]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/conversions?days=${days}`);
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load conversion data");
    } finally {
      setLoading(false);
    }
  };

  const fetchLandingPages = async () => {
    try {
      const res = await axios.get(`${API}/admin/event-landing-pages`);
      setLandingPages(res.data);
    } catch (err) {
      console.log("Could not load landing pages");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount || 0);
  };

  const StatCard = ({ title, value, icon: Icon, change, color = "#D4AF37" }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#121212] border border-[#27272A] rounded-lg p-6"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[#A1A1AA] text-sm mb-1">{title}</p>
          <p className="text-3xl font-bold text-[#F5F5F0]">{value}</p>
        </div>
        <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
      </div>
      {change !== undefined && (
        <div className={`flex items-center gap-1 mt-3 text-sm ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
          <span>{Math.abs(change)}% vs previous period</span>
        </div>
      )}
    </motion.div>
  );

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#F5F5F0] flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-[#D4AF37]" />
              Conversions Dashboard
            </h1>
            <p className="text-[#A1A1AA] mt-1">Track landing page performance and conversions</p>
          </div>
          
          <div className="flex items-center gap-3">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="px-4 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              onClick={fetchData}
              className="p-2 bg-[#27272A] text-[#F5F5F0] hover:bg-[#3F3F46] transition-colors rounded"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#D4AF37] border-t-transparent"></div>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard
                title="Total Page Views"
                value={data?.total_views?.toLocaleString() || "0"}
                icon={Eye}
                color="#3B82F6"
              />
              <StatCard
                title="Total Conversions"
                value={data?.total_conversions?.toLocaleString() || "0"}
                icon={ShoppingCart}
                color="#10B981"
              />
              <StatCard
                title="Conversion Rate"
                value={`${data?.conversion_rate || 0}%`}
                icon={Target}
                color="#D4AF37"
              />
              <StatCard
                title="Total Revenue"
                value={formatCurrency(data?.total_revenue)}
                icon={DollarSign}
                color="#8B5CF6"
              />
            </div>

            {/* Landing Pages Performance */}
            <div className="grid lg:grid-cols-2 gap-6 mb-8">
              {/* Pages Table */}
              <div className="bg-[#121212] border border-[#27272A] rounded-lg">
                <div className="p-4 border-b border-[#27272A]">
                  <h2 className="font-semibold text-[#F5F5F0] flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                    Landing Pages Performance
                  </h2>
                </div>
                <div className="divide-y divide-[#27272A]">
                  {landingPages.map(page => {
                    const pageData = data?.pages?.[page.slug] || { views: 0, conversions: 0, revenue: 0 };
                    const convRate = pageData.views > 0 ? ((pageData.conversions / pageData.views) * 100).toFixed(1) : 0;
                    
                    return (
                      <div key={page.id} className="p-4 hover:bg-[#0F0F0F] transition-colors">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-[#F5F5F0]">{page.title || page.slug}</span>
                            <span className={`px-2 py-0.5 text-xs rounded ${page.is_active ? 'bg-green-500/20 text-green-400' : 'bg-[#52525B]/20 text-[#a1a1aa]'}`}>
                              {page.is_active ? 'Active' : 'Draft'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/admin/heatmap/${page.slug}`}
                              className="p-1.5 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                              title="View Heatmap"
                            >
                              <MousePointer className="w-4 h-4" />
                            </Link>
                            <a
                              href={`/${page.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-[#a1a1aa]">Views</span>
                            <p className="text-[#F5F5F0] font-medium">{pageData.views}</p>
                          </div>
                          <div>
                            <span className="text-[#a1a1aa]">Conversions</span>
                            <p className="text-[#F5F5F0] font-medium">{pageData.conversions}</p>
                          </div>
                          <div>
                            <span className="text-[#a1a1aa]">Conv. Rate</span>
                            <p className="text-[#F5F5F0] font-medium">{convRate}%</p>
                          </div>
                          <div>
                            <span className="text-[#a1a1aa]">Revenue</span>
                            <p className="text-[#F5F5F0] font-medium">{formatCurrency(pageData.revenue)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {landingPages.length === 0 && (
                    <div className="p-8 text-center text-[#a1a1aa]">
                      No landing pages yet
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Bookings */}
              <div className="bg-[#121212] border border-[#27272A] rounded-lg">
                <div className="p-4 border-b border-[#27272A]">
                  <h2 className="font-semibold text-[#F5F5F0] flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-[#D4AF37]" />
                    Recent Conversions
                  </h2>
                </div>
                <div className="divide-y divide-[#27272A] max-h-[400px] overflow-y-auto">
                  {data?.recent_bookings?.map((booking, i) => (
                    <div key={i} className="p-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-[#F5F5F0]">{booking.name}</span>
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          booking.payment_status === 'completed' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {booking.payment_status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[#A1A1AA]">{booking.ticket_type} × {booking.quantity}</span>
                        <span className="text-[#D4AF37] font-medium">{formatCurrency(booking.total_amount)}</span>
                      </div>
                      <p className="text-xs text-[#a1a1aa] mt-1">
                        {new Date(booking.created_at).toLocaleDateString()} - {booking.page_slug}
                      </p>
                    </div>
                  ))}
                  {(!data?.recent_bookings || data.recent_bookings.length === 0) && (
                    <div className="p-8 text-center text-[#a1a1aa]">
                      No conversions yet
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="bg-[#121212] border border-[#27272A] rounded-lg p-6">
              <h2 className="font-semibold text-[#F5F5F0] mb-4">Quick Actions</h2>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/admin/landing-pages"
                  className="px-4 py-2 bg-[#D4AF37] text-[#050505] font-medium hover:bg-[#B8943B] transition-colors rounded"
                >
                  Manage Landing Pages
                </Link>
                <Link
                  to="/admin/ab-tests"
                  className="px-4 py-2 bg-[#27272A] text-[#F5F5F0] hover:bg-[#3F3F46] transition-colors rounded"
                >
                  A/B Tests
                </Link>
                <Link
                  to="/admin/pixel-settings"
                  className="px-4 py-2 bg-[#27272A] text-[#F5F5F0] hover:bg-[#3F3F46] transition-colors rounded"
                >
                  Pixel Settings
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default ConversionsDashboard;
